import React from "react";
import { Check, Minus } from "lucide-react";

const STATUS_STYLES = {
  active: {
    card: "bg-bansal-ink text-bansal-ivory border-bansal-ink shadow-sm",
    badge: "bg-bansal-gold text-bansal-ink",
    title: "text-bansal-ivory",
    hint: "text-bansal-ivory/70",
  },
  completed: {
    card: "bg-emerald-50 text-emerald-950 border-emerald-300/80 hover:bg-emerald-100/80",
    badge: "bg-emerald-600 text-white",
    title: "",
    hint: "text-emerald-800/80",
  },
  not_required: {
    card: "bg-slate-100/90 text-slate-600 border-slate-200 hover:bg-slate-100",
    badge: "bg-slate-400 text-white",
    title: "",
    hint: "text-slate-500",
  },
  pending: {
    card: "bg-amber-50/80 text-amber-950 border-amber-200 hover:border-amber-300",
    badge: "bg-card text-amber-900 border border-amber-300",
    title: "",
    hint: "text-amber-800/75",
  },
  locked: {
    card: "bg-muted/40 text-muted-foreground border-dashed border-border/70 cursor-not-allowed",
    badge: "bg-muted text-foreground/50 border border-border",
    title: "",
    hint: "text-muted-foreground",
  },
};

function resolveStatus(step, { active, done, reachable }) {
  if (active) return "active";
  if (step.status) return step.status;
  if (step.notRequired || step.autoComplete) return "not_required";
  if (done) return "completed";
  if (!reachable) return "locked";
  return "pending";
}

/**
 * Settlement wizard stepper — responsive grid; distinct colours per step state.
 */
export default function SettlementStepper({ steps, currentStep, completed, onSelect, testId }) {
  const completedSet = completed instanceof Set ? completed : new Set(completed || []);
  return (
    <ol
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2"
      role="tablist"
      data-testid={testId}
    >
      {steps.map((s) => {
        const done = completedSet.has(s.id);
        const active = currentStep === s.id;
        const reachable = !s.locked && (done || active || completedSet.has(s.id - 1) || s.id === 1);
        const status = resolveStatus(s, { active, done, reachable });
        const styles = STATUS_STYLES[status] || STATUS_STYLES.pending;
        const disabled = !onSelect || !reachable;

        return (
          <li key={s.id} className="min-w-0">
            <button
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => onSelect && reachable && onSelect(s.id)}
              data-testid={testId ? `${testId}-${s.id}` : undefined}
              data-step-status={status}
              className={`group flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-all min-h-11 ${styles.card}`}
            >
              <span
                className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-semibold shrink-0 mt-0.5 ${styles.badge}`}
              >
                {status === "completed" ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : status === "not_required" ? (
                  <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : (
                  s.id
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-[11px] sm:text-xs font-medium leading-snug ${styles.title}`}>
                  {s.label}
                </span>
                {s.hint && (
                  <span className={`block text-[10px] leading-snug mt-0.5 ${styles.hint}`}>
                    {s.hint}
                  </span>
                )}
                {status === "not_required" && (
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5 inline-block">
                    Not required
                  </span>
                )}
                {status === "pending" && s.optional && (
                  <span className="text-[9px] uppercase tracking-widest text-amber-700/80 mt-0.5 inline-block">
                    Optional
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
